/** C22 订阅结算：类型与本地 mock。路径对齐文档 §8，接入真实接口时只改本文件。 */

export const SettlementStatus = {
  Pending: 'pending',
  Confirmed: 'confirmed',
  Rejected: 'rejected',
  Closed: 'closed',
} as const
export type SettlementStatus = (typeof SettlementStatus)[keyof typeof SettlementStatus]

export const SettlementBillingType = {
  Workspace: 'workspace',
  Seat: 'seat',
  Usage: 'usage',
  Module: 'module',
} as const
export type SettlementBillingType =
  (typeof SettlementBillingType)[keyof typeof SettlementBillingType]

export const WalletType = {
  Hibro: 'hibro',
  External: 'external',
} as const
export type WalletType = (typeof WalletType)[keyof typeof WalletType]

export const WalletChain = {
  Ethereum: 'ethereum',
  Bnb: 'bnb',
  Tron: 'tron',
} as const
export type WalletChain = (typeof WalletChain)[keyof typeof WalletChain]

export const SettlementApiCode = {
  NotFound: 'NOT_FOUND',
  Insufficient: 'INSUFFICIENT',
} as const
export type SettlementApiCode = (typeof SettlementApiCode)[keyof typeof SettlementApiCode]

export const FilterAll = 'all' as const
export type FilterAll = typeof FilterAll

export const SETTLEMENT_STATUSES = [
  SettlementStatus.Pending,
  SettlementStatus.Confirmed,
  SettlementStatus.Rejected,
  SettlementStatus.Closed,
] as const

export const SETTLEMENT_BILLING_TYPES = [
  SettlementBillingType.Workspace,
  SettlementBillingType.Seat,
  SettlementBillingType.Usage,
  SettlementBillingType.Module,
] as const

export const WALLET_TYPES = [WalletType.Hibro, WalletType.External] as const
export const WALLET_CHAINS = [WalletChain.Ethereum, WalletChain.Bnb, WalletChain.Tron] as const

export type SettlementStatement = {
  id: string
  statement_no: string
  order_no: string
  app_name: string
  product_name: string
  billing_type: SettlementBillingType
  spec: string
  service_start: string
  service_end: string
  settlement_date: string
  amount: number
  status: SettlementStatus
  confirmed_at?: string
}

export type WithdrawBody = {
  wallet: WalletType
  amount: number
  chain?: WalletChain
  address?: string
}

export class SettlementApiError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.code = code
  }
}

let balance = 12800

const statements: SettlementStatement[] = [
  {
    id: 'stl_1',
    statement_no: 'STL-ORD-20260914-000801',
    order_no: 'ORD-20260914-000801',
    app_name: '项目管理',
    product_name: '专业版',
    billing_type: SettlementBillingType.Seat,
    spec: '20 人',
    service_start: '2026-09-14',
    service_end: '2026-11-14',
    settlement_date: '2026-10-14',
    amount: 1440,
    status: SettlementStatus.Confirmed,
    confirmed_at: '2026-10-14T10:02:18+08:00',
  },
  {
    id: 'stl_2',
    statement_no: 'STL-ORD-20260920-000803',
    order_no: 'ORD-20260920-000803',
    app_name: '项目管理',
    product_name: 'AI助手',
    billing_type: SettlementBillingType.Usage,
    spec: '标准包 · 5,000 次',
    service_start: '2026-09-20',
    service_end: '2026-10-20',
    settlement_date: '2026-10-14',
    amount: 36,
    status: SettlementStatus.Pending,
  },
  {
    id: 'stl_3',
    statement_no: 'STL-ORD-20261001-000805',
    order_no: 'ORD-20261001-000805',
    app_name: '项目管理',
    product_name: '效率套件',
    billing_type: SettlementBillingType.Module,
    spec: '高级报表、审计日志',
    service_start: '2026-10-01',
    service_end: '2026-12-01',
    settlement_date: '2026-11-15',
    amount: 432,
    status: SettlementStatus.Rejected,
  },
]

function wait<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(structuredClone(value)), 180)
  })
}

export async function getAvailableBalance() {
  return wait(balance)
}

export async function listSettlementStatements() {
  return wait(
    [...statements].sort((a, b) => b.settlement_date.localeCompare(a.settlement_date)),
  )
}

export async function getSettlementStatement(id: string) {
  const item = statements.find((s) => s.id === id)
  if (!item) throw new SettlementApiError(SettlementApiCode.NotFound, SettlementApiCode.NotFound)
  return wait(item)
}

export async function withdraw(body: WithdrawBody) {
  const latest = await getAvailableBalance()
  if (body.amount > latest) {
    throw new SettlementApiError(SettlementApiCode.Insufficient, SettlementApiCode.Insufficient)
  }
  balance = Number((latest - body.amount).toFixed(2))
  return wait({ ok: true as const, balance })
}
